import { MOCK_CONFIG, type Services } from '../config/mockConfig';
import { usePrivileges } from './usePrivilleges';

/**
 * Hook to manage mock configuration based on feature flags.
 *
 * @returns Object with `shouldUseMocks` function to check if mocks should be used for a service
 *
 * @example
 * ```tsx
 * const { shouldUseMocks } = useMocks();
 *
 * if (shouldUseMocks('projections')) {
 *   // Use mock data
 * } else {
 *   // Use real API
 * }
 * ```
 */
export const useMocks = () => {
  const { mocks } = usePrivileges();

  /**
   * Checks if mocks should be used for a specific service based on feature flags.
   *
   * @param serviceName - The name of the service
   * @returns true if mocks should be used, false otherwise
   */
  const shouldUseMocks = (serviceName: Services): boolean => {
    const serviceFlag = mocks.services[serviceName as keyof typeof mocks.services];
    const staticFlag = MOCK_CONFIG.services[serviceName as keyof typeof MOCK_CONFIG.services];

    // If static config has a specific flag, use it
    if (staticFlag !== undefined) {
      return staticFlag;
    }

    // If service has a specific flag, use it
    if (serviceFlag !== undefined) {
      return serviceFlag;
    }

    // Otherwise, use the global flag
    return mocks.useMocks;
  };

  return {
    shouldUseMocks,
    useMocksGlobally: mocks.useMocks,
    mocksConfig: mocks.services,
  };
};
